/**
 * MockBridgeTokensStore — web mirror of `Sources/Store/MockBridgeTokensStore.swift`.
 *
 * In-memory bridge-token store so the Setup screen renders standalone in MOCK
 * mode — the same way MockMemoriesStore backs the Brain tab. Seeds a couple of
 * fake existing tokens, and `mint` returns a fake `brdg_` plaintext ONCE (never
 * re-shown) and appends its metadata row, so the whole generate → list → revoke
 * flow is exercised offline with no network.
 */

import {
  BridgeTokensStoreError,
  type BridgeTokenInfo,
  type BridgeTokensStore,
  type MintedBridgeToken,
} from './BridgeTokensStore';

const LATENCY_MS = 250;
const delay = () => new Promise((r) => setTimeout(r, LATENCY_MS));

function isoAgo(minutes: number): string {
  return new Date(Date.now() - minutes * 60_000).toISOString();
}

function randomId(): string {
  // A stable-enough uuid-ish id; crypto.randomUUID where available.
  return globalThis.crypto?.randomUUID?.() ?? `mock-${Math.random().toString(16).slice(2)}`;
}

export class MockBridgeTokensStore implements BridgeTokensStore {
  private tokens: BridgeTokenInfo[] = [
    {
      id: '00000000-0000-4000-8000-00000000bt01',
      label: 'mini',
      executor: 'mini',
      created_at: isoAgo(60 * 24 * 3),
      last_used_at: isoAgo(0.4),
      revoked_at: null,
    },
    {
      id: '00000000-0000-4000-8000-00000000bt02',
      label: 'M4 Air',
      executor: 'air',
      created_at: isoAgo(60 * 24 * 9),
      last_used_at: isoAgo(60 * 26),
      revoked_at: null,
    },
    {
      id: '00000000-0000-4000-8000-00000000bt03',
      label: 'old laptop',
      executor: 'legacy',
      created_at: isoAgo(60 * 24 * 40),
      last_used_at: isoAgo(60 * 24 * 20),
      revoked_at: isoAgo(60 * 24 * 12),
    },
  ];

  async mint(opts?: { label?: string; executor?: string }): Promise<MintedBridgeToken> {
    await delay();
    const id = randomId();
    const now = new Date().toISOString();
    // A realistic-looking but entirely fake plaintext — NOT a real secret.
    const fakePlaintext = `brdg_mock_${randomId().replace(/-/g, '').slice(0, 32)}`;
    const info: BridgeTokenInfo = {
      id,
      label: opts?.label || 'New engine',
      executor: opts?.executor || null,
      created_at: now,
      last_used_at: null,
      revoked_at: null,
    };
    this.tokens = [info, ...this.tokens];
    return {
      token: fakePlaintext,
      id,
      label: info.label,
      created_at: now,
      note: 'Save this bridge token now — it is shown only once and never again.',
    };
  }

  async list(): Promise<BridgeTokenInfo[]> {
    await delay();
    return this.tokens;
  }

  async revoke(tokenId: string): Promise<void> {
    await delay();
    const idx = this.tokens.findIndex((t) => t.id === tokenId);
    if (idx < 0 || this.tokens[idx].revoked_at) {
      throw new BridgeTokensStoreError(`no active token ${tokenId}`, 404);
    }
    this.tokens = this.tokens.map((t, i) =>
      i === idx ? { ...t, revoked_at: new Date().toISOString() } : t,
    );
  }
}
